import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Map } from 'immutable'
import { useMemberHomeFirstTouchRecentOracleSessionsQuery } from '../../OraclePage/queries.generated'
import {
  APOLLO_CLIENT_ERROR_POLICY_ALL,
  APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
} from '../../../services/apollo-graphql/index'
import useMemberHomeFirstTouchSlide from './useMemberHomeFirstTouchSlide'
import {
  getMemberHomeFirstTouchContent,
  getMemberHomeFirstTouchTimeOfDay,
  MEMBER_HOME_FIRST_TOUCH_VARIATIONS,
  MOBILE_MAX_PX,
} from '../utils'

/**
 * Viewport gate, PM payload shape, Oracle prefetch, and swipe/shell sync for
 * the Member Home First Touch overlay.
 *
 * @param {Object} params
 * @param {boolean} params.featureEnabled  Build-time / config feature flag.
 * @param {import('immutable').Map} params.auth
 * @param {import('immutable').Map} params.firstTouchSection PM section payload.
 * @param {boolean} params.pageIsTouch Server-detected touch hint.
 * @param {boolean} params.viewportTouchable Client-detected touch capability.
 * @param {number} params.viewportWidth
 * @param {string} params.defaultOraclePersonalityId  Persisted user preference (feature tracking).
 * @param {(payload: {
 *   visible: boolean,
 *   variation?: string | null,
 *   timeOfDay?: string | null,
 * }) => void} params.setMemberHomeFirstTouch
 */
export default function useMemberHomeFirstTouchOverlayState ({
  featureEnabled,
  auth,
  firstTouchSection,
  pageIsTouch,
  viewportTouchable,
  viewportWidth,
  defaultOraclePersonalityId,
  setMemberHomeFirstTouch,
}) {
  const overlayRef = useRef(null)
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (!process.env.BROWSER || typeof window === 'undefined') {
      return false
    }
    return window.matchMedia(`(max-width: ${MOBILE_MAX_PX}px)`).matches
  })

  useEffect(() => {
    if (!process.env.BROWSER) {
      return undefined
    }
    const mq = window.matchMedia(`(max-width: ${MOBILE_MAX_PX}px)`)
    const syncViewport = () => setIsMobileViewport(mq.matches)
    syncViewport()
    mq.addEventListener('change', syncViewport)
    return () => mq.removeEventListener('change', syncViewport)
  }, [])

  const hasCoarsePointer = process.env.BROWSER && typeof window !== 'undefined'
    && window.matchMedia('(pointer: coarse)').matches
  const isTouchLikeDevice = !!pageIsTouch || !!viewportTouchable || hasCoarsePointer
  const isNarrowByViewportWidth = viewportWidth > 0 && viewportWidth <= MOBILE_MAX_PX
  const hasPmPayload = Map.isMap(firstTouchSection) && !firstTouchSection.isEmpty()
  const shouldShowOverlay = featureEnabled
    && hasPmPayload
    && (isMobileViewport || isNarrowByViewportWidth)
    && isTouchLikeDevice

  const timeOfDay = useMemo(() => getMemberHomeFirstTouchTimeOfDay(), [])

  const { data, sectionId } = getMemberHomeFirstTouchContent(firstTouchSection)
  const variation = Map.isMap(data)
    ? (data.get('displayMode') || MEMBER_HOME_FIRST_TOUCH_VARIATIONS.GENERIC)
    : null
  const showLastAiConversations = [
    MEMBER_HOME_FIRST_TOUCH_VARIATIONS.AI_FOCUSED,
    MEMBER_HOME_FIRST_TOUCH_VARIATIONS.AI_PLUS_GENERIC,
  ].includes(variation)
  const showAiPrompts = variation === MEMBER_HOME_FIRST_TOUCH_VARIATIONS.ENGAGEMENT
  const numberOfPrompts = variation === MEMBER_HOME_FIRST_TOUCH_VARIATIONS.AI_PLUS_GENERIC ? 1 : 3
  const showTiles = variation !== MEMBER_HOME_FIRST_TOUCH_VARIATIONS.AI_FOCUSED
  const showAiInput = variation !== MEMBER_HOME_FIRST_TOUCH_VARIATIONS.AI_VOICE_FOCUS

  const authJwt = Map.isMap(auth) ? auth.get('jwt') : undefined

  const recentOracleQuery = useMemberHomeFirstTouchRecentOracleSessionsQuery({
    skip: !shouldShowOverlay || !showLastAiConversations,
    variables: {
      limit: numberOfPrompts,
    },
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: {
      jwt: authJwt,
    },
  })

  const {
    overlayMostlyVisible,
    visuallyDismissed,
    showPullStrip,
    overlayStyle,
    onSlideTouchStart,
    onSlideTouchMove,
    onSlideTouchEnd,
    handleOverlayTransitionEnd,
    pullStripStyle,
  } = useMemberHomeFirstTouchSlide({
    showOverlay: shouldShowOverlay,
    sectionId,
    overlayRef,
  })

  const isShellChromeVisible = !!(
    shouldShowOverlay && sectionId && Map.isMap(data) && overlayMostlyVisible
  )

  // Sync the Redux slice that drives header chrome whenever overlay visibility
  // or variation changes. No cleanup here — clearing on every dep change would
  // dispatch `{ visible: false }` between renders and cause flicker.
  useEffect(() => {
    setMemberHomeFirstTouch({
      visible: isShellChromeVisible,
      variation,
      timeOfDay: isShellChromeVisible ? timeOfDay : null,
    })
  }, [isShellChromeVisible, variation, timeOfDay, setMemberHomeFirstTouch])

  // Reset the slice only when this hook actually unmounts.
  useEffect(
    () => () => setMemberHomeFirstTouch({ visible: false }),
    [setMemberHomeFirstTouch],
  )

  const oraclePersonalityId = defaultOraclePersonalityId

  return {
    overlayRef,
    shouldShowOverlay,
    sectionId,
    data,
    timeOfDay,
    variation,
    showLastAiConversations,
    showAiPrompts,
    showTiles,
    showAiInput,
    recentOracleSessions: recentOracleQuery.data?.oracleChatSessionList?.oracleChatSessions ?? [],
    visuallyDismissed,
    showPullStrip,
    overlayStyle,
    onSlideTouchStart,
    onSlideTouchMove,
    onSlideTouchEnd,
    handleOverlayTransitionEnd,
    pullStripStyle,
    oraclePersonalityId,
  }
}
